
import { axiosInstance } from '../../App';
import { ComboItem, SurgeryKitValidationDTO, SurgeryPartDTO, SurgeryStockBalanceDTO, SurgeryStockItemComboDTO } from '../../interface/surgery';
import { getNomenclators } from '../FavoriteStudies/useFavoriteStudiesService';

export function getSurgeryPart(surgeryAppointmentId: number, surgeryId?: number): Promise<SurgeryPartDTO | null> {
    return axiosInstance.get('/api/secured/surgery/getSurgeryPart', {
        params: {
            surgeryAppointmentId,
            ...(surgeryId !== undefined ? { surgeryId } : {})
        }
    })
    .then(response => {
        return response.data;
    })
    .catch(error => {
        console.error('Error al obtener el parte quirúrgico:', error);
        return null;
    });
}

export function getSectors(hospitalLocationId: number): Promise<ComboItem[]> {
    return axiosInstance.get('/api/secured/surgery/getSectors', {
        params: { hospitalLocationId }
    })
    .then(response => {
        return response.data;
    })
    .catch(error => {
        console.error('Error al obtener sectores:', error);
        return [];
    });
}

export function getStockItems(surgeryAppointmentId: number, inputText: string, offset: number, pageSize: number): Promise<SurgeryStockItemComboDTO[]> {
    return axiosInstance.get('/api/secured/surgery/getStockItems', {
        params: {
            surgeryAppointmentId,
            inputText,
            offset,
            pageSize
        }
    })
    .then(response => {
        return response.data;
    })
    .catch(error => {
        console.error('Error al obtener insumos:', error);
        return [];
    });
}

export function validateKit(kitId: number, quantity: number, surgeryAppointmentId: number): Promise<SurgeryKitValidationDTO | null> {
    return axiosInstance.get('/api/secured/surgery/validateKit', {
        params: {
            kitId,
            quantity,
            surgeryAppointmentId
        }
    })
    .then(response => {
        return response.data;
    })
    .catch(error => {
        if (error?.response?.status === 409) {
            return error.response.data as SurgeryKitValidationDTO;
        }
        console.error('Error al validar kit:', error);
        return null;
    });
}

/**
 * Existencia actual del insumo en el sector. Si el sitio tiene desactivado el control de stock,
 * `controlActive` es false y no hay que limitar la carga. `null` si no se pudo consultar.
 */
export function getStockBalance(surgeryAppointmentId: number, stockItemPresentationId: number): Promise<SurgeryStockBalanceDTO | null> {
    return axiosInstance.get('/api/secured/surgery/getStockBalance', {
        params: { surgeryAppointmentId, stockItemPresentationId }
    })
    .then(response => {
        return response.data as SurgeryStockBalanceDTO;
    })
    .catch(error => {
        console.error('Error al consultar el saldo del insumo:', error);
        return null;
    });
}

export function getProfessionals(inputText: string, offset: number, pageSize: number): Promise<ComboItem[]> {
    return axiosInstance.get('/api/secured/surgery/getProfessionals', {
        params: {
            inputText,
            offSet: offset,
            pageSize
        }
    })
    .then(response => {
        // SurgeryController returns ProfessionalDTO: getter getProfessional() → JSON key "professional"
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (response.data ?? []).map((p: any) => ({
            id: Number(p.professional),
            name: `${p.lastname ?? ''} ${p.firstname ?? ''}`.trim()
        }));
    })
    .catch(error => {
        console.error('Error al obtener profesionales:', error);
        return [];
    });
}

export function getProfessionalRoles(): Promise<ComboItem[]> {
    return axiosInstance.get('/api/secured/surgery/getProfessionalRoles')
    .then(response => {
        return response.data;
    })
    .catch(error => {
        console.error('Error al obtener roles de profesionales:', error);
        return [];
    });
}

export function getLegacyAnesthesiaOptions(): Promise<ComboItem[]> {
    return axiosInstance.get('/api/secured/surgery/getLegacyAnesthesiaOptions')
    .then(response => {
        return response.data;
    })
    .catch(error => {
        console.error('Error al obtener opciones de anestesia (formato anterior):', error);
        return [];
    });
}

export function getAnesthesiaTypeOptions(): Promise<ComboItem[]> {
    return axiosInstance.get('/api/secured/surgery/getAnesthesiaTypeOptions')
    .then(response => {
        return response.data;
    })
    .catch(error => {
        console.error('Error al obtener tipos de anestesia:', error);
        return [];
    });
}

export function getAnesthesiaTechniqueOptions(anesthesiaTypeId: number): Promise<ComboItem[]> {
    return axiosInstance.get('/api/secured/surgery/getAnesthesiaTechniqueOptions', {
        params: { anesthesiaTypeId }
    })
    .then(response => {
        return response.data;
    })
    .catch(error => {
        console.error('Error al obtener técnicas de anestesia:', error);
        return [];
    });
}

export function getAnesthesiaProcedureOptions(): Promise<ComboItem[]> {
    return axiosInstance.get('/api/secured/surgery/getAnesthesiaProcedureOptions')
    .then(response => {
        return response.data;
    })
    .catch(error => {
        console.error('Error al obtener procedimientos de anestesia:', error);
        return [];
    });
}

export function getSurgicalRiskOptions(): Promise<ComboItem[]> {
    return axiosInstance.get('/api/secured/surgery/getSurgicalRiskOptions')
    .then(response => {
        return response.data;
    })
    .catch(error => {
        console.error('Error al obtener opciones de riesgo quirúrgico:', error);
        return [];
    });
}

export function saveSurgeryPart(data: SurgeryPartDTO): Promise<number | null> {
    return axiosInstance.post('/api/secured/surgery/saveSurgeryPart', data)
    .then(response => {
        return response.data;
    })
    .catch(error => {
        console.error('Error al guardar el parte quirúrgico:', error);
        throw error;
    });
}

export function getNomenclatorsForHealthEntity(
    inputText: string,
    offset: number,
    pageSize: number,
    healthEntity?: number,
    serviceDate?: string,
): Promise<ComboItem[]> {
    return axiosInstance.get('/api/secured/surgery/getNomenclatorsForHealthEntity', {
        params: {
            inputText,
            offset,
            pageSize,
            healthEntity,
            serviceDate,
        }
    })
    .then(response => {
        return response.data;
    })
    .catch(error => {
        console.error('Error al obtener nomencladores por obra social:', error);
        return [];
    });
}

export function useSurgeryPartService() {
  return {
    getSurgeryPart,
    getSectors,
    getStockItems,
    getProfessionals,
    getProfessionalRoles,
    getAnesthesiaTypeOptions,
    getAnesthesiaTechniqueOptions,
    getAnesthesiaProcedureOptions,
    getSurgicalRiskOptions,
    saveSurgeryPart,
    getNomenclators,
  }
}
